import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import {
  createVendorCommissionDefault,
  fetchVendorCommissionDefaults,
  updateVendorCommissionDefault,
  VendorCommissionDefault,
} from '../../services/MasterAdmin/vendorService';

const ROLE_NAMES: Record<number, string> = {
  1: 'Hospital',
  2: 'Clinic',
  3: 'Diagnostic Center',
  4: 'Medical Store',
  5: 'Ambulance Agency',
  6: 'Blood Bank',
  8: 'Product Partner',
};

const DEFAULT_VENDOR_TYPES = [1, 2, 3, 4, 5, 6, 8];

type FormRow = {
  vendorType: number;
  roleName: string;
  commissionRate: string;
  taxPercent: string;
  isCommissionActive: boolean;
  existing: VendorCommissionDefault | null;
  saving: boolean;
};

const VendorCommissionManagementPage: React.FC = () => {
  const [rows, setRows] = useState<FormRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const initialRows = useMemo<FormRow[]>(
    () =>
      DEFAULT_VENDOR_TYPES.map((vendorType) => ({
        vendorType,
        roleName: ROLE_NAMES[vendorType] || `Vendor Type ${vendorType}`,
        commissionRate: '0',
        taxPercent: '18',
        isCommissionActive: true,
        existing: null,
        saving: false,
      })),
    []
  );

  useEffect(() => {
    const loadDefaults = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetchVendorCommissionDefaults();
        const defaults = response?.data || [];

        const mapped = initialRows.map((row) => {
          const existing = defaults.find((d) => Number(d.vendorType) === row.vendorType) || null;
          if (!existing) return row;
          return {
            ...row,
            commissionRate: String(existing.commissionValue ?? 0),
            taxPercent: String(existing.taxPercent ?? 18),
            isCommissionActive: Boolean(existing.isCommissionActive),
            existing,
          };
        });
        setRows(mapped);
      } catch (e: any) {
        setError(e?.response?.data?.message || 'Failed to load vendor commission defaults');
        setRows(initialRows);
      } finally {
        setLoading(false);
      }
    };

    loadDefaults();
  }, [initialRows]);

  const updateRow = (vendorType: number, patch: Partial<FormRow>) => {
    setRows((prev) =>
      prev.map((row) => (row.vendorType === vendorType ? { ...row, ...patch } : row))
    );
  };

  const handleSave = async (row: FormRow) => {
    const commissionRate = Number(row.commissionRate);
    const taxPercent = Number(row.taxPercent);

    if (Number.isNaN(commissionRate) || commissionRate < 0 || commissionRate > 100) {
      setError(`Invalid commission for ${row.roleName}. Use 0 to 100.`);
      return;
    }
    if (Number.isNaN(taxPercent) || taxPercent < 0) {
      setError(`Invalid tax percent for ${row.roleName}.`);
      return;
    }

    try {
      setError(null);
      setSuccessMessage(null);
      updateRow(row.vendorType, { saving: true });

      if (row.existing) {
        const response = await updateVendorCommissionDefault(row.vendorType, {
          commissionRate,
          taxPercent,
          isCommissionActive: row.isCommissionActive,
          isActive: true,
        });
        updateRow(row.vendorType, {
          existing: response.data,
          saving: false,
        });
      } else {
        const response = await createVendorCommissionDefault({
          vendorType: row.vendorType,
          commissionRate,
          taxPercent,
          isCommissionActive: row.isCommissionActive,
        });
        updateRow(row.vendorType, {
          existing: response.data,
          saving: false,
        });
      }

      setSuccessMessage(`Commission saved for ${row.roleName}`);
    } catch (e: any) {
      updateRow(row.vendorType, { saving: false });
      setError(e?.response?.data?.message || `Failed to save ${row.roleName} commission`);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
          Vendor Commission Management
        </h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
          Configure default commission by vendor type. New vendors automatically pick these values at registration.
        </p>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-900/20 dark:text-red-300">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700 dark:border-green-900/60 dark:bg-green-900/20 dark:text-green-300">
          {successMessage}
        </div>
      )}

      <div className="overflow-hidden rounded-lg bg-white shadow dark:bg-gray-800 dark:shadow-gray-900/30">
        <div className="ma-table-scroll scrollbar-light">
          <table className="w-full min-w-[45rem] divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700/70">
              <tr>
                <th className="w-[25%] min-w-[10rem] px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-300">
                  Vendor Type
                </th>
                <th className="w-[20%] min-w-[8rem] px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-300">
                  Commission %
                </th>
                <th className="w-[20%] min-w-[8rem] px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-300">
                  Tax %
                </th>
                <th className="w-[15%] min-w-[7rem] px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-300">
                  Active
                </th>
                <th className="w-[20%] min-w-[8rem] px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-300">
                  Action
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/80">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-10">
                    <div className="flex items-center justify-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading default commissions...
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.vendorType}>
                    <td className="px-4 py-4 text-sm font-medium text-gray-900 dark:text-white">
                      {row.roleName}
                    </td>
                    <td className="px-4 py-4">
                      <input
                        type="number"
                        min={0}
                        max={100}
                        step="0.01"
                        value={row.commissionRate}
                        onChange={(e) =>
                          updateRow(row.vendorType, { commissionRate: e.target.value })
                        }
                        className="w-32 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      />
                    </td>
                    <td className="px-4 py-4">
                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        value={row.taxPercent}
                        onChange={(e) =>
                          updateRow(row.vendorType, { taxPercent: e.target.value })
                        }
                        className="w-32 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      />
                    </td>
                    <td className="px-4 py-4">
                      <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                        <input
                          type="checkbox"
                          checked={row.isCommissionActive}
                          onChange={(e) =>
                            updateRow(row.vendorType, { isCommissionActive: e.target.checked })
                          }
                        />
                        Enabled
                      </label>
                    </td>
                    <td className="px-4 py-4">
                      <button
                        onClick={() => handleSave(row)}
                        disabled={row.saving}
                        className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
                      >
                        {row.saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        {row.existing ? 'Update' : 'Create'}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default VendorCommissionManagementPage;
